import React, { useState, useEffect, useRef } from "react";
import { FaCheck, FaMinus, FaStar, FaTimes, FaEraser, FaGlobe } from "react-icons/fa";
import styles from "../css/css_pages/ChecklistPopup.module.css";
import Cookies from "js-cookie";
import PopupMessage from "../layouts/PopupMessage";
import config from "../../config";
import SignatureCanvas from "react-signature-canvas";
import { useTranslation } from 'react-i18next';

const ChecklistPopup = ({ checklist, onClose }) => {
  const { t, i18n } = useTranslation();
  const [checklistItems, setChecklistItems] = useState([]);
  const [clientNote, setClientNote] = useState("");
  const [alertMessage, setAlertMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedComment, setSelectedComment] = useState("");
  const [isChecklistUpdated, setIsChecklistUpdated] = useState(false);
  const [signature, setSignature] = useState(null);
  const [language, setLanguage] = useState(i18n.language === 'ta' ? 'tamil' : 'sinhala');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const sigCanvas = useRef(null);
  const [lastTap, setLastTap] = useState(0);

  useEffect(() => {
    if (checklist && checklist.schedule_id) {
      fetchChecklistData(language);
      fetchChecklist(checklist.schedule_id);
    }
  }, [checklist, language]);

  const showPopupMessage = (message, type = "error") => {
    setAlertMessage(message);
    setMessageType(type);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const fetchChecklistData = async (selectedLanguage) => {
    const token = Cookies.get("authToken");
    try {
      const response = await fetch(
        `${config.API_URL}/client/get-checklist-data/${selectedLanguage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(t('fetch_checklist_data_error'));
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(t('fetch_checklist_data_error_details'), error);
      showPopupMessage(t('fetch_checklist_data_error'), "error");
    }
  };

  const fetchChecklist = async (scheduleId) => {
    const token = Cookies.get("authToken");
    try {
      const response = await fetch(
        `${config.API_URL}/client/get-checklist?schedule_id=${scheduleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(t('fetch_checklist_error'));
      }

      const data = await response.json();

      let tsoTicked = {};
      let clientTicked = {};

      try {
        tsoTicked =
          data[0].tso_ticked && typeof data[0].tso_ticked === "string"
            ? JSON.parse(data[0].tso_ticked.replace(/'/g, '"'))
            : data[0].tso_ticked || {};

        clientTicked =
          data[0].client_ticked && typeof data[0].client_ticked === "string"
            ? JSON.parse(data[0].client_ticked.replace(/'/g, '"'))
            : data[0].client_ticked || {};
      } catch (parseError) {
        console.error(t('parse_error'), parseError);
        return;
      }

      // Fetch checklist data in the selected language
      const checklistData = await fetchChecklistData(language);
      
      const mappedChecklistItems = checklistData.map((item) => ({
        id: item.number,
        description: item.description,
        tso: tsoTicked[item.number] || 0, // Default to 0 (cross) if not set for TSO
        client: clientTicked[item.number] || 2, // Default to 2 (dash) if not set for client
      }));

      setChecklistItems(mappedChecklistItems);
      setIsChecklistUpdated(
        Object.values(clientTicked).some((value) => value !== 2)
      );
    } catch (error) {
      console.error(t('fetch_checklist_error_details'), error);
      showPopupMessage(t('fetch_checklist_error'), "error");
    }
  };

  const handleLanguageChange = async (newLanguage) => {
    setLanguage(newLanguage);
    setShowLanguageDropdown(false);
    
    // Fetch checklist data in the new language
    const checklistData = await fetchChecklistData(newLanguage);
    
    if (checklistData) {
      // Update checklist items with new descriptions
      setChecklistItems(prevItems => 
        prevItems.map(item => {
          const newItem = checklistData.find(i => i.number === item.id);
          return newItem ? {...item, description: newItem.description} : item;
        })
      );
    }
  };

  const handleDoubleTap = (id) => {
    const now = Date.now();
    const DOUBLE_PRESS_DELAY = 300;

    if (lastTap && (now - lastTap) < DOUBLE_PRESS_DELAY) {
      // Double tap detected
      setChecklistItems(prevItems =>
        prevItems.map(item =>
          item.id === id
            ? {
                ...item,
                client: item.client === 0 ? 2 : 0 // Toggle between 0 (cross) and 2 (dash)
              }
            : item
        )
      );
    }
    setLastTap(now);
  };

  const toggleChecklistItem = (id) => {
    if (isChecklistUpdated) return;

    setChecklistItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? {
              ...item,
              client: item.client === 2 ? 1 : // If dash, change to check
                       item.client === 1 ? 0 : // If check, change to cross
                       2 // If cross, change to dash
            }
          : item
      )
    );
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setSignature(null);
    }
  };

  const validateChecklist = () => {
    // Check if any item is still in dash state (not marked)
    const hasUnmarkedItems = checklistItems.some(item => item.client === 2);
    
    if (hasUnmarkedItems) {
      showPopupMessage(t('all_items_must_be_marked'), "error");
      return false;
    }
    
    return true;
  };

  const updateChecklistStatus = async () => {
    // Validate checklist first
    if (!validateChecklist()) {
      return;
    }

    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      showPopupMessage(t('signature_required'), "error");
      return;
    }

    const signatureData = sigCanvas.current.toDataURL("image/png");
    setSignature(signatureData);

    showPopupMessage(t('signature_saved'), "success");

    const token = Cookies.get("authToken");
    const checklistUpdates = {};
    checklistItems.forEach((item) => {
      checklistUpdates[item.id] = item.client;
    });

    if (!clientNote) {
      showPopupMessage(t('client_note_required'), "error");
      return;
    }

    if (rating === 0) {
      showPopupMessage(t('rating_required'), "error");
      return;
    }

    if (rating <= 3 && !selectedComment) {
      showPopupMessage(t('comment_required'), "error");
      return;
    }

    if (rating > 3) {
      setSelectedComment(" ");
    }

    try {
      const formData = new FormData();
      formData.append("schedule_id", checklist.schedule_id);
      formData.append("checklist_updates", JSON.stringify(checklistUpdates));
      formData.append("client_note", clientNote);
      formData.append("rating", rating);
      formData.append("comment", selectedComment);

      const blob = await fetch(signatureData).then((res) => res.blob());
      formData.append("signature", blob, "signature.png");

      const response = await fetch(
        `${config.API_URL}/client/update-checklist-status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(t('update_checklist_error'));
      }

      showPopupMessage(t('checklist_updated'), "success");
      setIsChecklistUpdated(true);
      setTimeout(() => {
        onClose();
      }, 2000);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error(t('update_checklist_error_details'), error);
      showPopupMessage(t('update_checklist_error'), "error");
    }
  };

  const handleStarClick = (selectedRating) => {
    setRating(selectedRating);
  };

  const comments = [
    t('comment_slow_service'),
    t('comment_unfriendly_staff'),
    t('comment_poor_quality'),
    t('comment_issues_not_resolved'),
    t('comment_other'),
  ];

  const renderStatusIcon = (status, isTso = false) => {
    // For TSO, we only show cross (0) or check (1), no dash
    if (isTso) {
      return status === 1 ? (
        <FaCheck className={styles.iconDone} />
      ) : (
        <FaTimes className={styles.iconCross} />
      );
    }
    
    // For client, show all three states
    switch (status) {
      case 0: // Cross
        return <FaTimes className={styles.iconCross} />;
      case 1: // Check
        return <FaCheck className={styles.iconDone} />;
      case 2: // Dash
      default:
        return <FaMinus className={styles.iconPending} />;
    }
  };

  return (
    <>
      {alertMessage && (
        <PopupMessage
          message={alertMessage}
          type={messageType}
          onClose={closePopupMessage}
        />
      )}
      <div className={styles.popupOverlay}>
        <div className={styles.popupContent}>
          <div className={styles.popupHeader}>
            <h2>{t('checklist_for_schedule')} {checklist?.schedule_no}</h2>
            <div className={styles.headerControls}>
              <div className={styles.languageSelector}>
                <button 
                  className={styles.languageToggle}
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                >
                  <FaGlobe />
                  <span>{language === 'tamil' ? 'தமிழ்' : 'සිංහල'}</span>
                </button>
                {showLanguageDropdown && (
                  <div className={styles.languageDropdown}>
                    <button 
                      onClick={() => handleLanguageChange('sinhala')}
                      className={language === 'sinhala' ? styles.activeLanguage : ''}
                    >
                      සිංහල
                    </button>
                    <button 
                      onClick={() => handleLanguageChange('tamil')}
                      className={language === 'tamil' ? styles.activeLanguage : ''}
                    >
                      தமிழ்
                    </button>
                  </div>
                )}
              </div>
              <button className={styles.closeButton} onClick={onClose}>
                <FaTimes />
              </button>
            </div>
          </div>

          <div className={styles.checklistContainer}>
            <div className={styles.checklistTableContainer}>
              <table className={styles.checklistTable}>
                <thead>
                  <tr>
                    <th className={styles.idColumn}>{t('id')}</th>
                    <th className={styles.descriptionColumn}>{t('description')}</th>
                    <th className={styles.statusColumn}>{t('tso')}</th>
                    <th className={styles.statusColumn}>{t('client')}</th>
                  </tr>
                </thead>
                <tbody>
                  {checklistItems.map((item) => (
                    <tr key={item.id}>
                      <td className={styles.idColumn}>{item.id}</td>
                      <td className={styles.descriptionColumn}>{item.description}</td>
                      <td
                        className={`${styles.statusCell} ${styles.disabledCell}`}
                        title={t('tso_disabled_tooltip')}
                      >
                        <div className={styles.statusIndicator}>
                          {renderStatusIcon(item.tso, true)} {/* Pass true for isTso */}
                        </div>
                      </td>
                      <td className={styles.statusCell}>
                        <button
                          className={`${styles.statusButton} ${
                            isChecklistUpdated ? styles.disabledButton : ''
                          }`}
                          onClick={() => toggleChecklistItem(item.id)}
                          onTouchStart={() => handleDoubleTap(item.id)}
                          disabled={isChecklistUpdated}
                        >
                          <div className={styles.statusIndicator}>
                            {renderStatusIcon(item.client)}
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {checklist.schedule_status !== "closed" && !isChecklistUpdated && (
              <div className={styles.formSection}>
                <div className={styles.formGroup}>
                  <label>{t('client_note_placeholder')}</label>
                  <textarea
                    rows="3"
                    value={clientNote}
                    onChange={(e) => setClientNote(e.target.value)}
                    className={styles.clientNote}
                    placeholder={t('client_note_placeholder')}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>{t('rate_service')}:</label>
                  <div className={styles.ratingContainer}>
                    {[...Array(5)].map((_, index) => {
                      const ratingValue = index + 1;
                      return (
                        <FaStar
                          key={index}
                          className={`${styles.star} ${
                            ratingValue <= (hoverRating || rating) ? styles.starFilled : ''
                          }`}
                          onMouseEnter={() => setHoverRating(ratingValue)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => handleStarClick(ratingValue)}
                        />
                      );
                    })}
                    <span className={styles.ratingText}>
                      {rating > 0 && `${rating} ${rating === 1 ? t('star') : t('stars')}`}
                    </span>
                  </div>
                </div>

                {rating > 0 && rating <= 3 && (
                  <div className={styles.formGroup}>
                    <label>{t('reason_for_low_rating')}</label>
                    <select
                      value={selectedComment}
                      onChange={(e) => setSelectedComment(e.target.value)}
                      className={styles.commentSelect}
                    >
                      <option value="" disabled>
                        {t('select_comment')}
                      </option>
                      {comments.map((comment, index) => (
                        <option key={index} value={comment}>
                          {comment}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label>{t('sign_here')}</label>
                  <div className={styles.signatureWrapper}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: styles.signatureCanvas,
                      }}
                    />
                    <button
                      onClick={clearSignature}
                      className={styles.clearSignatureButton}
                    >
                      <FaEraser /> {t('clear_signature')}
                    </button>
                  </div>
                </div>

                {checklist.schedule_status === "completed" && (
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.submitButton}
                      onClick={updateChecklistStatus}
                    >
                      {t('save_confirm')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChecklistPopup;